import React, { ChangeEvent } from 'react';
import cx from 'classnames';
import { connect } from 'react-redux';
import { ReactComponent as CrossSVG } from '../../../assets/sightx-icons/svg/x-close.svg';
import { ReactComponent as AlertSVG } from '../../../assets/sightx-icons/svg/alert-triangle.svg';
import { RootState } from '../../../slices/rootReducer';
import Icon from '@ant-design/icons';
import { Button, Modal } from 'antd';
import { AddUsersInBulkForm } from '../common/addUsersInBulkForm';
import { FormInstance } from 'antd/lib/form';
import { isValidString as isNotEmptyString } from '../../common/utils/string';
import {
  actions,
  EmailStatusType,
  selectors as userBulkCreationSelectors,
  UserErrorCode
} from '../../../slices/users/bulkCreation';
import {
  checkEmail,
  createUsersInBulk,
  ICheckEmailPayload,
  IUserBulkCreationPayload,
  IUserForBulkCreation
} from '../../../slices/users/bulkCreation/index.thunks';
import { AccessTypes } from '../../common/domain/accountAccessType';
import { fetchAccountById } from '../../../slices/accounts/accounts.thunks';

import styles from './addUsersInBulkModal.module.scss';

interface IState {
  usersToAddList: IUserForBulkCreation[];
  isAddToListEnabled: boolean;
}

interface IStateProps {
  emailStatus: EmailStatusType | null;
  loading: boolean;
  errors: string[];
  errorMessage: string;
  emailsWithErrors: Set<string>;
}

interface IDispatchProps {
  checkEmail: (arg: ICheckEmailPayload) => ReturnType<ReturnType<typeof checkEmail>>;
  createUsersInBulk: (arg: IUserBulkCreationPayload) => ReturnType<ReturnType<typeof createUsersInBulk>>;
  fetchAccountById: (arg: string) => ReturnType<ReturnType<typeof fetchAccountById>>;
  clearValues: typeof actions.clearValues;
  clearCheckEmailValues: typeof actions.clearCheckEmailValues;
  setEmailIsAlreadyAddedToList: typeof actions.setEmailIsAlreadyAddedToList;
}

interface IProps {
  onClose: () => void;
  availableSeats: number;
  accountId: string;
  isForSxAdmin: boolean;
  accountType: string;
  isPurchasingSeats?: boolean;
}

type AddUsersInBulkModalProps = IStateProps & IDispatchProps & IProps;
class AddUsersInBulkModalBase extends React.Component<AddUsersInBulkModalProps, IState> {
  private readonly formRef: React.RefObject<FormInstance>;

  constructor(props: AddUsersInBulkModalProps) {
    super(props);
    this.state = {
      usersToAddList: [],
      isAddToListEnabled: false
    };
    this.handleOnEmailChange = this.handleOnEmailChange.bind(this);
    this.handleAddToList = this.handleAddToList.bind(this);
    this.handleSendInvites = this.handleSendInvites.bind(this);
    this.updateIsAddToListEnabled = this.updateIsAddToListEnabled.bind(this);
    this.addUserAndResetForm = this.addUserAndResetForm.bind(this);
    this.formRef = React.createRef<FormInstance>();
    this.handleOnUserListChange = this.handleOnUserListChange.bind(this);
  }

  componentDidUpdate(prevProps: AddUsersInBulkModalProps, prevState: IState) {
    const { errors, emailsWithErrors } = this.props;
    if (errors && errors.length > 0 && this.state.usersToAddList.every((user) => !emailsWithErrors.has(user.email))) {
      this.props.clearValues();
    }
    if (prevProps.loading && !this.props.loading && this.props.errors.length === 0 &&
      this.props.emailsWithErrors.size === 0 && !this.props.errorMessage) {
      this.props.onClose();
      this.props.fetchAccountById(this.props.accountId);
    }
  }

  componentWillUnmount() {
    this.props.onClose();
    this.props.clearValues();
  }

  private updateIsAddToListEnabled() {
    const areSeatsAvailable = this.props.availableSeats - this.state.usersToAddList.length > 0;
    const formValues = this.formRef.current?.getFieldsValue();
    const areFieldsFilled = !!formValues.firstName && !!formValues.lastName && !!formValues.email;
    this.setState({ isAddToListEnabled: areSeatsAvailable && areFieldsFilled });
  }

  private handleSendInvites() {
    const { accountId } = this.props;
    const { usersToAddList } = this.state;
    this.props.createUsersInBulk({ accountId, users: usersToAddList });
  }

  private async handleAddToList(user: IUserForBulkCreation) {
    const userToAdd: IUserForBulkCreation = { ...user };
    const isAlreadyAdded = this.state.usersToAddList.some(u => u.email === user.email);
    if (isAlreadyAdded) {
      this.props.setEmailIsAlreadyAddedToList();
      return;
    }
    const isValidFirstName = isNotEmptyString(user.firstName);
    if (!isValidFirstName) {
      this.resetFormField('firstName');
    }
    const isValidLastName = isNotEmptyString(user.lastName);
    if (!isValidLastName) {
      this.resetFormField('lastName');
    }
    userToAdd.firstName = user.firstName.trim();
    userToAdd.lastName = user.lastName.trim();
    this.formRef.current?.validateFields();
    if (isValidFirstName && isValidLastName) {
      await this.props.checkEmail({ email: userToAdd.email });
      const { emailStatus } = this.props;
      if (emailStatus === EmailStatusType.Valid) {
        this.addUserAndResetForm(userToAdd);
      }
    }
  }

  private addUserAndResetForm(userToAdd: IUserForBulkCreation) {
    this.setState((prevState) => {
      return {
        usersToAddList: [...prevState.usersToAddList, userToAdd],
        formValues: { firstName: '', lastName: '', email: '', isOwner: false },
        errorMessage: '',
        isAddToListEnabled: false
      };
    }, () => {
      if (this.formRef.current) {
        this.formRef.current.resetFields();
        this.updateIsAddToListEnabled();
      }
    });
  }

  private resetFormField = (fieldName: string) => {
    if (this.formRef.current) {
      this.formRef.current.setFieldsValue({
        [fieldName]: ''
      });
    }
  };

  private handleOnEmailChange(event: ChangeEvent<HTMLInputElement>) {
    const value = event.currentTarget.value.trim();
    this.formRef.current?.setFieldsValue({ email: value });
    this.updateIsAddToListEnabled();
    const { emailStatus } = this.props;
    if (emailStatus) {
      this.props.clearCheckEmailValues();
    }
  }

  private handleOnUserListChange(usersToAddList: IUserForBulkCreation[]) {
    this.setState({
      usersToAddList: [...usersToAddList]
    });
  }

  private renderContent() {
    const {
      loading,
      isPurchasingSeats
    } = this.props;

    const { usersToAddList } = this.state;
    return <>
      <div className={styles.errorContainer}>
        {this.renderSendInvitesError()}
      </div>
      {this.renderTitle()}
      <AddUsersInBulkForm
        accountId={this.props.accountId}
        isForSxAdmin={this.props.isForSxAdmin}
        isPurchasingSeats={this.props.isPurchasingSeats}
        onUserListChange={this.handleOnUserListChange}
      />
      <div className={cx(styles.formRow, styles.footer)}>
        <Button
          type="default"
          onClick={this.props.onClose}
          className={styles.cancelButton}
        >
          {isPurchasingSeats ? 'Skip for now' : 'Cancel'}
        </Button>
        <Button
          type="primary"
          onClick={this.handleSendInvites}
          className={styles.submitButton}
          loading={loading}
          disabled={usersToAddList.length <= 0}
        >
          Send invites
        </Button>
      </div>
    </>;
  }

  private renderTitle() {
    const { accountType } = this.props;
    const title = accountType === AccessTypes.Enterprise ? 'Invite users' : 'Assign new licenses';

    return <div className={styles.title}>
      {title}
    </div>;
  }

  private renderSendInvitesError() {
    const { errors, errorMessage } = this.props;
    if ((!errors || errors.length === 0) && !errorMessage) {
      return null;
    }
    if (errors && errors.length > 0) {
      return errors.map((error) => (
        <div key={error} className={styles.sendInvitesError}>
          <div className={styles.alertContainer}>
            <Icon className={styles.alertIcon} component={AlertSVG} />
          </div>
          <span className={cx(styles.text)}>{error}</span>
        </div>
      ));
    }
    if (errorMessage) {
      return (
        <div className={styles.sendInvitesError}>
          <div className={styles.alertContainer}>
            <Icon className={styles.alertIcon} component={AlertSVG} />
          </div>
          <span className={cx(styles.text)}>{errorMessage}</span>
        </div>
      );
    }
  }

  render() {
    const { onClose } = this.props;
    return (
      <Modal
        visible={true}
        footer={null}
        onCancel={onClose}
        className={cx('modal-detail', styles.addUsersInBulkModal)}
        closeIcon={<Icon className={styles.closeIcon} component={CrossSVG} />}
        centered
        maskClosable={false}
        width={450}
      >
        {this.renderContent()}
      </Modal>
    );
  }
}

const getErrorMessage = (errorCode: UserErrorCode, otherMessage: string): string => {
  switch (errorCode) {
  case UserErrorCode.EmailAlreadyExists:
    return 'Some emails already have a SightX account';
  case UserErrorCode.InvalidEmail:
    return 'Some emails have an invalid format';
  case UserErrorCode.InvalidFirstName:
    return 'Some users have an invalid first name';
  case UserErrorCode.InvalidLastName:
    return 'Some users have an invalid last name';
  default:
    return otherMessage;
  }
};

const mapStateToProps = (state: RootState): IStateProps => {
  const userBulkCreationErrors = userBulkCreationSelectors.getErrors(state);
  const userBulkCreationErrorMessage = userBulkCreationSelectors.getMessage(state);
  const errors = userBulkCreationErrors.map(errorCode => getErrorMessage(errorCode, userBulkCreationErrorMessage));
  return {
    emailStatus: userBulkCreationSelectors.getEmailStatus(state),
    loading: userBulkCreationSelectors.getLoading(state),
    errors,
    errorMessage: userBulkCreationErrorMessage,
    emailsWithErrors: new Set<string>(userBulkCreationSelectors.getEmailsWithErrors(state) ?? [])
  };
};

export const AddUsersInBulkModal = connect(mapStateToProps, {
  checkEmail,
  createUsersInBulk,
  clearValues: actions.clearValues,
  clearCheckEmailValues: actions.clearCheckEmailValues,
  setEmailIsAlreadyAddedToList: actions.setEmailIsAlreadyAddedToList,
  fetchAccountById
})(AddUsersInBulkModalBase);
